<template>
	<div
		v-if="lifeStageData && categories && savings && debts"
		:class="[
			'dashboard-page',
			'card-page',
			{ overflow: showDashboardTutorial || isModalOpened }
		]"
	>
		<ja-header
			:title="dasbboardMetadata.title"
			:subtitle="dasbboardMetadata.subtitle"
			:show-intro="!showDashboardTutorial"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/home"></svgicon>
			</template>
		</ja-header>
		<div class="dashboard-content">
			<div class="buttons-section">
				<ja-headsup-control-buttons
					class="mb--2"
					:complete-life-stage-button-disabled="completeLifeStageButtonDisabled"
					:finalize-shopping-visibility="finalizeShoppingVisibility"
					:disabled="disabled"
					@toggleCompleteLSBtnState="$emit('toggleCompleteLSBtnState')"
					@toggleFinalizeBudgetModal="$emit('toggleFinalizeBudgetModal')"
					@toggleFinalizeShoppingModal="$emit('toggleFinalizeShoppingModal')"
				/>
			</div>
			<div class="sidebar-container">
				<ja-calendar-container
					use-tutorial-id="profile"
					class="bg--white"
					container-only
					title="profile"
				>
					<div class="sidebar-container__section">
						<div class="section__content">
							<div class="d-flex">
								<ja-avatar
									:size="128"
									role="button"
									@mousedown.prevent
								></ja-avatar>
								<div class="profile-data">
									<div v-for="field in shortProfileData" :key="field.label">
										<span class="label text--sm text--uppercase text--bold-600">
											{{ field.label }}
										</span>
										<span class="value text--bold-500">{{ field.value }}</span>
									</div>
								</div>
							</div>
							<ja-future-tracker
								class="mt--2"
								:children="profileData.studentChildren"
								:num-pets="profileData.numberOfPets"
								:married="isMarried"
							></ja-future-tracker>
						</div>
					</div>
					<div class="sidebar-container__section">
						<div class="section__content display-contents">
							<ja-budget-counter></ja-budget-counter>
							<ja-financial-health
								v-if="!profileData.isEntryVirtualSimulationType"
							></ja-financial-health>
						</div>
					</div>
					<div
						v-if="
							isAutoLoanBankingAndFinance ||
								isMortgageLoanVisible ||
								isAutoLoanDealershipVisible
						"
						class="sidebar-container__section"
					>
						<div class="section__content content--center btn-group">
							<button
								v-if="isAutoLoanDealershipVisible"
								:disabled="isAutoLoanDisabled"
								class="btn btn-link text--bold-600"
								@click="openLoan(AUTO_LOAN)"
							>
								<svgicon
									class="icon--x-large"
									:class="isAutoLoanDealerShipApplied && 'icon--success'"
									name="expenseCategories/car-dealership"
								></svgicon>
							</button>
							<button
								v-if="isAutoLoanBankingAndFinance"
								:disabled="isAutoLoanDisabled"
								class="btn btn-link text--bold-600"
								@click="openLoan(AUTO_LOAN_BANKING_AND_FINANCE)"
							>
								<svgicon
									class="icon--x-large"
									:class="isAutoLoanBankingApplied && 'icon--success'"
									name="expenseCategories/car-bank"
								></svgicon>
							</button>
							<button
								v-if="isMortgageLoanVisible"
								:disabled="isMortgageLoanDisabled"
								class="btn btn-link text--bold-600"
								@click="openLoan(MORTGAGE_LOAN)"
							>
								<svgicon
									class="icon--x-large"
									:class="isMortgageLoanApplied && 'icon--success'"
									name="expenseCategories/mortgage-loan"
								></svgicon>
							</button>
						</div>
					</div>
					<div class="sidebar-container__section">
						<div class="section__content content--center">
							<button class="btn btn-link text--bold-600" @click="goToProfile">
								{{ $t('viewProfile') }}
							</button>
						</div>
					</div>
				</ja-calendar-container>
				<div class="sponsor-section mt--2">
					<ja-national-sponsor></ja-national-sponsor>
					<ja-national-sponsor
						use-secondary
						:use-label="false"
					></ja-national-sponsor>
				</div>
			</div>
			<div class="content-wrapper">
				<content ref="pageContent">
					<ja-calendar-container
						ref="savings"
						toggable
						use-tutorial-id="savings"
						:title="$t('savingsTitle')"
						:subtitle="$t('savingsSubtitle')"
						:amount-spent="savingsExpenses"
					>
						<div class="calendar-dashboard" role="list">
							<ja-dashboard-item
								v-for="item in savings"
								:key="item.studentGoalId"
								:item="item"
								class="dashboard-item"
								role="listitem"
							>
								<template v-slot:balance-info>
									{{ $t('accountBalance') }}:
									{{ item.accountBalance | currency }}
								</template>
								<template v-slot:icon>
									<svgicon
										class="done"
										name="done"
										aria-hidden="true"
									></svgicon>
								</template>
							</ja-dashboard-item>
						</div>
					</ja-calendar-container>
					<ja-calendar-container
						v-if="!lifeStageIsTeenager"
						:title="$t('debtTitle')"
						:subtitle="$t('minimumPaymentDue')"
						toggable
						:collapsed="showDashboardTutorial"
						:amount-spent="debtsExpenses"
					>
						<div class="calendar-dashboard" role="list">
							<ja-dashboard-item
								:item="debts.creditCard"
								class="dashboard-item"
								role="listitem"
							>
								<template v-slot:balance-info>
									<div>
										<span class="credit-balance" name="currentBalance"
											>{{ $t('currentBalance') }}:
											{{ balanceCreditCard | currency }}</span
										>
										<span class="credit-balance" name="creditLimit"
											>{{ $t('availableCredit') }}:
											{{ debts.creditCard.balance | currency }}</span
										>
										<span class="credit-limit" name="creditLimit"
											>{{ $t('creditLimit') }}:
											{{ debts.creditCard.creditLimit | currency }}</span
										>
									</div>
								</template>
								<template v-slot:icon>
									<button
										class="tooltip-btn"
										:aria-label="$t('moreInfoCreditCard')"
										@click="toggleDebtTooltip('creditCard')"
										@mousedown.prevent
									>
										<svgicon
											class="info"
											name="info"
											aria-hidden="true"
										></svgicon>
									</button>
									<ja-tooltip
										v-show="showCreditCardTooltip"
										class="debt-tooltip right"
										><div class="body-text-1">
											{{ $t('debtTooltip', { typeOfDebt: 'credit card' }) }}
										</div>
										<ja-button
											class="small transparent"
											name="credit-done"
											@click.native="toggleDebtTooltip('creditCard')"
											>{{ $t('okay') }}</ja-button
										></ja-tooltip
									>
								</template>
							</ja-dashboard-item>
							<ja-dashboard-item
								v-if="!educationIsFree"
								ref="categoriesDashboard"
								:item="debts.educationLoan"
								class="dashboard-item"
								role="listitem"
							>
								<template v-slot:balance-info>
									<div>
										{{ $t('originalLoanAmount') }}:
										{{ debts.educationLoan.totalBalance | currency }}
									</div>
								</template>
								<template v-slot:icon>
									<button
										class="tooltip-btn"
										:aria-label="$t('moreInfoPostLoan')"
										@click="toggleDebtTooltip('postSecondaryLoans')"
										@mousedown.prevent
									>
										<svgicon
											class="info"
											name="info"
											aria-hidden="true"
										></svgicon>
									</button>
									<ja-tooltip
										v-show="showEducationLoanTooltip"
										class="debt-tooltip right"
										><div class="body-text-1">
											{{ $t('debtTooltip', { typeOfDebt: 'education loan' }) }}
										</div>
										<ja-button
											class="small transparent"
											name="education-done"
											@click.native="toggleDebtTooltip('postSecondaryLoans')"
											>{{ $t('okay') }}</ja-button
										>
									</ja-tooltip>
								</template>
							</ja-dashboard-item>
							<ja-dashboard-item-loan
								v-if="hasMortageLoan && debts.mortgageLoan.amount > 0"
								ref="categoriesDashboard"
								:item="debts.mortgageLoan"
								class="dashboard-item"
								role="listitem"
							>
								<template v-slot:balance-info
									><div>
										<span class="credit-balance">
											{{ $t('originalLoanAmount') }}:
											{{ debts.mortgageLoan.totalBalance | currency }}
										</span>
										<span class="credit-limit" name="currentBalance"
											>{{ $t('outstandingBalance') }}:
											{{ debts.mortgageLoan.remainingBalance | currency }}
										</span>
									</div>
								</template>
								<template v-slot:icon>
									<button
										class="tooltip-btn"
										:aria-label="$t('moreInfoPostLoan')"
										@click="toggleDebtTooltip('mortgageLoan')"
										@mousedown.prevent
									>
										<svgicon
											class="info"
											name="info"
											aria-hidden="true"
										></svgicon>
									</button>
									<ja-tooltip
										v-show="showMortgageLoanTooltip"
										class="debt-tooltip right"
										><div class="body-text-1">
											{{
												$t('loanTooltip', { typeOfDebt: $t('mortgageLoan') })
											}}
										</div>
										<ja-button
											class="small transparent"
											name="mortgage-done"
											@click.native="toggleDebtTooltip('mortgageLoan')"
											>{{ $t('okay') }}</ja-button
										>
									</ja-tooltip>
								</template>
							</ja-dashboard-item-loan>
							<ja-dashboard-item-loan
								v-if="hasAutoLoan && debts.autoLoan.amount > 0"
								ref="categoriesDashboard"
								:item="debts.autoLoan"
								class="dashboard-item"
								role="listitem"
							>
								<template v-slot:balance-info
									><div>
										<span class="credit-balance">
											{{ $t('originalLoanAmount') }}:

											{{ debts.autoLoan.totalBalance | currency }}</span
										>
										<span class="credit-limit" name="currentBalance"
											>{{ $t('outstandingBalance') }}:
											{{ debts.autoLoan.remainingBalance | currency }}
										</span>
									</div>
								</template>
								<template v-slot:icon>
									<button
										class="tooltip-btn"
										:aria-label="$t('moreInfoPostLoan')"
										@click="toggleDebtTooltip('autoLoanBankingAndFinance')"
										@mousedown.prevent
									>
										<svgicon
											class="info"
											name="info"
											aria-hidden="true"
										></svgicon>
									</button>
									<ja-tooltip
										v-show="showAutoLoanTooltip"
										class="debt-tooltip right"
										><div class="body-text-1">
											{{
												$t('loanTooltip', {
													typeOfDebt: $t('autoLoanBankingAndFinance')
												})
											}}
										</div>
										<ja-button
											class="small transparent"
											name="auto-done"
											@click.native="
												toggleDebtTooltip('autoLoanBankingAndFinance')
											"
											>{{ $t('okay') }}</ja-button
										>
									</ja-tooltip>
								</template>
							</ja-dashboard-item-loan>
							<ja-dashboard-item-loan
								v-if="
									hasAutoLoanDealership && debts.autoLoanDealership.amount > 0
								"
								ref="categoriesDashboard"
								:item="debts.autoLoanDealership"
								class="dashboard-item"
								role="listitem"
							>
								<template v-slot:balance-info
									><div>
										<span class="credit-balance">
											{{ $t('originalLoanAmount') }}:
											{{
												debts.autoLoanDealership.totalBalance | currency
											}}</span
										>
										<span class="credit-limit" name="currentBalance"
											>{{ $t('outstandingBalance') }}:
											{{
												debts.autoLoanDealership.remainingBalance | currency
											}}</span
										>
									</div>
								</template>
								<template v-slot:icon>
									<button
										class="tooltip-btn"
										:aria-label="$t('moreInfoPostLoan')"
										@click="toggleDebtTooltip('autoLoanDealership')"
										@mousedown.prevent
									>
										<svgicon
											class="info"
											name="info"
											aria-hidden="true"
										></svgicon>
									</button>
									<ja-tooltip
										v-show="showAutoLoanDealershipTooltip"
										class="debt-tooltip right"
										><div class="body-text-1">
											{{
												$t('loanTooltip', {
													typeOfDebt: $t('autoLoanDealership')
												})
											}}
										</div>
										<ja-button
											class="small transparent"
											name="auto-dealership-done"
											@click.native="toggleDebtTooltip('autoLoanDealership')"
											>{{ $t('okay') }}</ja-button
										>
									</ja-tooltip>
								</template>
							</ja-dashboard-item-loan>
						</div>
					</ja-calendar-container>
					<ja-calendar-container
						:title="$t('expensesTitle')"
						:subtitle="$t('expensesSubtitle')"
						:amount-spent="categoriesExpenses"
						role="list"
					>
						<ja-expense-card
							v-for="category in categories"
							:key="category.expenseCategory_ID"
							class="calendar-card"
							use-click-event
							use-tag
							use-icon
							use-lock
							use-tutorial-id="expenses"
							:category="category"
							@click.native="selectCategory(category)"
						></ja-expense-card>
					</ja-calendar-container>
				</content>
			</div>
		</div>
		<div v-if="showDashboardTutorial" class="onboarding-wrapper"></div>
	</div>
</template>
<script>
import { LifeStageData, ProfileData, ExpenseCategories } from '@/mixins';
import Header from '@/components/ui/headers/Header';
import CalendarContainer from '@/components/Calendar/CalendarContainer';
import DashboardItem from '@/components/Calendar/DashboardItem';
import DashboardItemLoan from '@/components/Calendar/DashboardItemLoan';
import ExpenseCard from '@/components/Calendar/ExpenseCard';
import BudgetCounter from '@/components/Metrics/BudgetCounter';
import FinancialHealth from '@/components/Metrics/FinancialHealthMeter';
import { Avatar, Button, Tooltip, NationalSponsor } from '@/components/ui';
import FutureTracker from '@/components/HeadsupDisplay/FutureTracker';
import HeadsupControlButtons from '@/components/HeadsupDisplay/HeadsupControlButtons';
import {
	applicationLoanTypes,
	debtNames,
	maritalStatus
} from '@/helpers/constants';

export default {
	components: {
		jaHeader: Header,
		jaCalendarContainer: CalendarContainer,
		jaDashboardItem: DashboardItem,
		jaDashboardItemLoan: DashboardItemLoan,
		jaExpenseCard: ExpenseCard,
		jaBudgetCounter: BudgetCounter,
		jaFinancialHealth: FinancialHealth,
		jaButton: Button,
		jaTooltip: Tooltip,
		jaNationalSponsor: NationalSponsor,
		jaAvatar: Avatar,
		jaFutureTracker: FutureTracker,
		jaHeadsupControlButtons: HeadsupControlButtons
	},
	mixins: [LifeStageData, ExpenseCategories, ProfileData],
	props: {
		isModalOpened: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		completeLifeStageButtonDisabled: {
			type: Boolean,
			default: false
		},
		finalizeShoppingVisibility: {
			type: Boolean,
			default: false
		},
		isTutorialVisible: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			showCreditCardTooltip: false,
			showEducationLoanTooltip: false,
			showMortgageLoanTooltip: false,
			showAutoLoanTooltip: false,
			showAutoLoanDealershipTooltip: false,
			AUTO_LOAN: applicationLoanTypes.AUTO_LOAN,
			AUTO_LOAN_BANKING_AND_FINANCE:
				applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE,
			MORTGAGE_LOAN: applicationLoanTypes.MORTGAGE_LOAN
		};
	},
	computed: {
		isMarried() {
			return this.profileData.cmsMaritalStatus.status.toLowerCase() ===
				this.$t('married') ||
				this.profileData.cmsMaritalStatus.status.toLowerCase() ===
					maritalStatus.MARRIED.toLowerCase()
				? true
				: false;
		},
		creditCardInterest() {
			return this.lifeStageData.creditCardInterest;
		},
		purchases() {
			return this.lifeStageIsYA
				? this.lifeStageData.creditCardDebt - this.creditCardStartingBalance
				: this.lifeStageData.creditCardDebt;
		},
		balanceCreditCard() {
			let balance = this.purchases + this.creditCardInterest;
			if (this.lifeStageIsYA) {
				balance += this.creditCardStartingBalance;
			}
			return balance;
		},
		savings() {
			return this.$store.getters.savings;
		},
		debts() {
			return this.$store.getters.debts;
		},
		categoriesExpenses() {
			if (this.filteredCategories) {
				return this.filteredCategories.reduce((sum, item) => {
					if (item.amountSpentForCurrentStudent >= 0) {
						return sum + parseInt(item.amountSpentForCurrentStudent);
					}
				}, 0);
			}

			return 0;
		},
		savingsExpenses() {
			if (this.savings) {
				return this.savings.reduce((sum, item) => {
					if (item.amount >= 0) {
						return sum + parseInt(item.amount);
					}
				}, 0);
			}

			return 0;
		},
		debtsExpenses() {
			if (this.debts) {
				let totalDebs = 0;
				for (const property in this.debts) {
					totalDebs += this.debts[property].amount;
				}

				return totalDebs;
			}

			return 0;
		},
		showDashboardTutorial() {
			return !this.userData || this.isTutorialVisible;
		},
		userData() {
			return this.$store.getters.userData;
		},
		shortProfileData() {
			return [
				{
					label: this.$t('name'),
					value:
						this.lifeStageData.formStudentName ??
						`${this.userData.firstName} ${this.userData.lastName}`
				},
				{
					label: this.$t('lifeStage'),
					value: this.profileData.cmsLifeStage.name
				}
			];
		},
		dasbboardMetadata() {
			const defaultMetadata = {
				title: this.$t('shoppingTitle'),
				subtitle: this.$t('dashboardShoppingSubtitle')
			};

			if (this.isInBudgetingPhase) {
				defaultMetadata.title = this.$t('dashboardBudgetingTitle');
				defaultMetadata.subtitle = this.$t('dashboardBudgetingSubtitle');
			}

			return defaultMetadata;
		},
		isAutoLoanBankingAndFinance() {
			return this.$store.getters.isAutoLoanFinanceVisible;
		},
		isAutoLoanDealershipVisible() {
			return this.$store.getters.isAutoLoanDealershipVisible;
		},
		isMortgageLoanVisible() {
			return this.$store.getters.isMortgageLoanVisible;
		},
		isAutoLoanDisabled() {
			return (
				!this.$store.getters.isInBudgetingPhase &&
				!this.$store.getters.isAutoLoanApplied
			);
		},
		isAutoLoanDealerShipApplied() {
			return this.$store.getters.isAutoLoanAppliedDealerShip;
		},
		isAutoLoanBankingApplied() {
			return this.$store.getters.isAutoLoanAppliedBanking;
		},
		isMortgageLoanApplied() {
			return this.$store.getters.isMortgageLoanApplied;
		},
		isMortgageLoanDisabled() {
			return (
				!this.$store.getters.isInBudgetingPhase &&
				!this.$store.getters.isMortgageLoanApplied
			);
		}
	},
	watch: {
		filteredCategories() {
			this.$store.commit('setLoading', false);
		}
	},
	created() {
		this.$store.dispatch('getProfileData');
		this.$store.dispatch('getInitialDebt');
		this.checkForProfileData();
		this.checkForLifeStageData();
		this.checkForCategories();
	},
	beforeCreate() {
		this.$store.commit('setLoading', true);
	},
	mounted() {
		if (this.lifeStageData && this.profileData) {
			this.$store.commit('setLoading', false);
		}
	},
	methods: {
		toggleDebtTooltip(debtName) {
			if (debtName == debtNames.CREDIT_CARD) {
				this.showEducationLoanTooltip = false;
				this.showAutoLoanTooltip = false;
				this.showMortgageLoanTooltip = false;
				this.showAutoLoanDealershipTooltip = false;
				this.showCreditCardTooltip = !this.showCreditCardTooltip;
				if (this.showCreditCardTooltip) {
					this.$announcer.polite(
						this.$t('debtTooltip', { typeOfDebt: this.$t('creditCard') })
					);
				}
			} else if (debtName == debtNames.EDUCATION) {
				this.showCreditCardTooltip = false;
				this.showAutoLoanTooltip = false;
				this.showMortgageLoanTooltip = false;
				this.showAutoLoanDealershipTooltip = false;
				this.showEducationLoanTooltip = !this.showEducationLoanTooltip;
				if (this.showEducationLoanTooltip) {
					this.$announcer.polite(
						this.$t('debtTooltip', { typeOfDebt: this.$t('educationLoan') })
					);
				}
			} else if (debtName == debtNames.MORTGAGE_LOAN) {
				this.showCreditCardTooltip = false;
				this.showEducationLoanTooltip = false;
				this.showAutoLoanTooltip = false;
				this.showAutoLoanDealershipTooltip = false;
				this.showMortgageLoanTooltip = !this.showMortgageLoanTooltip;
				if (this.showMortgageLoanTooltip) {
					this.$announcer.polite(
						this.$t('debtTooltip', { typeOfDebt: this.$t('mortgageLoan') })
					);
				}
			} else if (debtName == debtNames.AUTO_LOAN) {
				this.showCreditCardTooltip = false;
				this.showEducationLoanTooltip = false;
				this.showMortgageLoanTooltip = false;
				this.showAutoLoanDealershipTooltip = false;
				this.showAutoLoanTooltip = !this.showAutoLoanTooltip;
				if (this.showAutoLoanTooltip) {
					this.$announcer.polite(
						this.$t('debtTooltip', {
							typeOfDebt: this.$t('autoLoanBankingAndFinance')
						})
					);
				}
			} else {
				this.showCreditCardTooltip = false;
				this.showEducationLoanTooltip = false;
				this.showMortgageLoanTooltip = false;
				this.showAutoLoanTooltip = false;
				this.showAutoLoanDealershipTooltip = !this
					.showAutoLoanDealershipTooltip;
				if (this.showAutoLoanDealershipTooltip) {
					this.$announcer.polite(
						this.$t('debtTooltip', {
							typeOfDebt: this.$t('autoLoanDealership')
						})
					);
				}
			}
		},
		selectCategory(category) {
			if (!category.isLockedForCurrentStudent) {
				this.$emit('categorySelected', category);
			}
		},
		goToProfile() {
			this.$store.commit('setLoading', true);
			this.$router.push({ name: 'profile' });
		},
		openLoan(loanType) {
			this.$emit(`categorySelected`, this.$store.getters[loanType]);
		}
	}
};
</script>
<style lang="scss" scoped>
.dashboard-page {
	.dashboard-content {
		display: flex;
		position: relative;
		width: 100%;
		padding: 32px 5%;
		flex-wrap: wrap;
		justify-content: space-between;

		.buttons-section {
			flex-basis: 100%;
		}
	}

	.sidebar-container {
		max-width: 400px;
		margin-right: 16px;
		flex: 0 1 auto;

		&__section {
			width: 100%;
			padding: 16px;
			background-color: $container-bgcolor;

			&:not(:last-of-type) {
				.section__content {
					padding-bottom: 24px;
					border-bottom: 1px solid $divider-color;
				}
			}

			img {
				display: block;
				max-height: 48px;
				margin-top: 4px;
			}

			svg:not([class^='icon--']) {
				height: 20px;
				width: 20px;
			}
		}

		.section__content {
			display: flex;
			flex-wrap: wrap;
			width: 100%;

			// TODO: Quick fix! Need to be fixed
			&.display-contents {
				::v-deep.radial-progress-bar:not(:last-of-type) {
					margin-bottom: 16px;
				}

				::v-deep.icon-wrapper {
					display: contents;
				}
			}
		}
	}

	.sticky {
		position: sticky;
		align-self: flex-start;
		top: 32px;
	}

	.content-wrapper {
		position: relative;
		flex-basis: 75%;
		display: flex;
		flex-direction: column;
		flex: 1;

		content {
			margin: auto;

			.calendar-container {
				margin: auto;

				.calendar-dashboard {
					position: relative;
					width: 100%;
					background-color: #ffffff;
					border-radius: 6px;

					.dashboard-item {
						&:not(:last-child) {
							border-bottom: 1px solid rgba(220, 220, 220, 0.44);
						}
						.credit-balance {
							padding-right: 8px;
							margin-right: 8px;
							border-right: 1px solid $font-dark;
						}
						.border-text {
							border: 2px solid $btn-career-selected;
							padding: 0 5px 0 5px;
							color: $btn-career-selected;
						}
						.debt-tooltip {
							top: 10px;
							right: 64px;
							width: 320px;
							text-align: center;
							transform: translateY(-50%);
							padding: 32px 32px 24px 32px;

							button {
								height: auto;
								margin-top: 24px;
							}
						}

						.tooltip-btn {
							padding: 0;
							background-color: transparent;
							border: 0;
						}
						svg {
							height: 20px;
							width: 20px;

							&.info {
								fill: $primary-state;

								&:hover {
									cursor: pointer;
									fill: $hover-state;
								}
							}
							&.done {
								fill: $icon-read;
							}
						}
					}
				}

				&.expense-card-wrapper {
					&.space-bottom {
						margin-bottom: 128px;
					}
					::v-deep content {
						padding-right: 0;
					}
				}

				&:not(:last-child) {
					margin-bottom: 16px;
				}

				.calendar-card {
					width: calc(25% - 8px);
					margin-bottom: 8px;
					margin-right: 8px;
				}
			}
		}
	}
}
// TODO: Might be removed
.tooltip {
	transform: translateZ(1px);
	-webkit-transform: translateZ(1px);
	z-index: 999;
	width: 300px;
	text-align: center;
	padding: 24px 24px 16px 24px;
	left: 0;
	right: 0;
	top: 0;
	margin: auto;
	outline: 0;

	h1 {
		margin: 0;
	}

	@media (min-width: $breakpoint-mid) {
		width: 360px;
	}

	button {
		height: auto;
		margin-top: 24px;
	}
}

.onboarding-wrapper {
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 10;
	background-color: rgba(54, 62, 82, 0.6);
}

.sponsor-section {
	div {
		padding-bottom: 18px;
	}

	::v-deep.logo {
		max-height: 64px;
	}
}

.future-tracker {
	margin: 0 auto;
	background-color: $page-bgcolor;
}

.profile-data {
	font-family: 'RobotoMedium';
	padding: 0 32px;
	.label,
	.value {
		display: block;
		padding: 8px;
	}
}

.top-offset {
	margin-top: 36px;
}

.buttons-section {
	text-align: right;
}

.overflow {
	position: fixed;
	width: 100%;
	height: 100%;
}
</style>
